/**
 * services/api.js
 * Centralized API client for ReArch Executive Reporting.
 *
 * All calls go through the `request()` helper which:
 *   - Sends JSON by default
 *   - Throws `ApiError` on non-2xx responses with the server's error message
 *   - Includes credentials (cookies) for SWA auth
 *
 * Backend: Azure Functions at /api (proxied locally via Vite to port 7071)
 */

import { getAuthContextHeaders } from '@/services/auth.js'

const GET_CACHE_TTL_MS = 60 * 1000
const getCache = new Map()

// ---- Error type ----

/**
 * Structured API error carrying HTTP status and server message.
 */
export class ApiError extends Error {
  /**
   * @param {number} status  HTTP status code
   * @param {string} message Human-readable error from server or default
   */
  constructor(status, message) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

// ---- Core fetch wrapper ----

function cloneResponse(data) {
  if (data == null) return data
  if (typeof structuredClone === 'function') return structuredClone(data)
  return JSON.parse(JSON.stringify(data))
}

function getCacheScope(headers = {}) {
  return [
    headers['x-exec-reporting-test-role'] || '',
    headers['x-exec-reporting-test-user-email'] || '',
    headers['x-user-email'] || '',
  ].join('|')
}

function clearGetCache() {
  getCache.clear()
}

function looksLikeEmail(value) {
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(value || '').trim())
}

function numericOrNull(value) {
  if (value == null || value === '') return null
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : null
}

function dateOnly(value) {
  if (!value) return null
  const text = String(value).slice(0, 10)
  const date = new Date(`${text}T00:00:00Z`)
  return Number.isNaN(date.getTime()) ? null : date
}

function addDaysToDate(dateStr, days) {
  const date = dateOnly(dateStr)
  if (!date) return null
  date.setUTCDate(date.getUTCDate() + (Number(days) || 0))
  return date.toISOString().slice(0, 10)
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max)
}

export function calculateBillingPercentComplete(data = {}) {
  const billings = numericOrNull(data.billingsToDate)
  const contractValue = numericOrNull(data.contractValueWithCOs ?? data.currentContractValue ?? data.originalContractValue)
  if (billings == null || contractValue == null || contractValue <= 0) return null
  return (billings / contractValue) * 100
}

/**
 * Calculate elapsed contract time through a supplied cutoff date.
 * Dashboard callers omit the cutoff to use today; historical billing rows pass
 * their exact Billing Through Date so each percentage remains reproducible.
 */
export function calculateTimePercentComplete(data = {}, asOfDate = new Date()) {
  const start = dateOnly(data.contractStartDate || data.actualStartDate)
  const finishDate = data.currentContractFinish
    || addDaysToDate(
      data.originalContractFinish || data.originalContractFinishDate || data.contractedFinishDate,
      data.daysAddedByCO ?? data.daysAddedByChangeOrder
    )
  const finish = dateOnly(finishDate)
  if (asOfDate instanceof Date && Number.isNaN(asOfDate.getTime())) return null
  const currentValue = asOfDate instanceof Date ? asOfDate.toISOString() : asOfDate
  const current = dateOnly(currentValue)
  if (!start || !finish || !current || finish <= start) return null
  return clamp(((current - start) / (finish - start)) * 100, 0, 100)
}

export function revenueTrend(currentValue, previousValue) {
  const current = numericOrNull(currentValue)
  const previous = numericOrNull(previousValue)
  if (current == null || previous == null) return { direction: 'none', diff: null }
  const diff = current - previous
  if (Math.abs(diff) < 0.01) return { direction: 'flat', diff: 0 }
  return { direction: diff > 0 ? 'up' : 'down', diff }
}

export function clearApiCache() {
  clearGetCache()
}

async function cachedGet(path, ttlMs = GET_CACHE_TTL_MS) {
  const authHeaders = await getAuthContextHeaders()
  const cacheKey = `${getCacheScope(authHeaders)}::${path}`
  const cached = getCache.get(cacheKey)
  if (cached && cached.expiresAt > Date.now()) return cloneResponse(cached.data)

  const data = await request(path, { method: 'GET', headers: authHeaders })
  getCache.set(cacheKey, {
    data: cloneResponse(data),
    expiresAt: Date.now() + ttlMs,
  })
  return cloneResponse(data)
}

/**
 * Low-level fetch wrapper.
 *
 * @param {string} path    API path, e.g. '/api/projects'
 * @param {RequestInit} [options]  Fetch options override
 * @returns {Promise<any>} Parsed JSON response body
 * @throws {ApiError} On non-2xx HTTP response
 */
async function request(path, options = {}) {
  const defaultHeaders = {
    'Content-Type': 'application/json',
    ...await getAuthContextHeaders(),
  }

  const res = await fetch(path, {
    credentials: 'include',
    ...options,
    headers: {
      ...defaultHeaders,
      ...(options.headers || {}),
    },
  })

  // 204 No Content or DELETE with no body
  if (res.status === 204) return null

  let body
  try {
    body = await res.json()
  } catch {
    body = null
  }

  if (!res.ok) {
    const serverError = body?.error
    const msg = typeof serverError === 'string'
      ? serverError
      : serverError?.message || body?.message || `HTTP ${res.status}`
    throw new ApiError(res.status, msg)
  }

  return body && Object.prototype.hasOwnProperty.call(body, 'data') ? body.data : body
}

/**
 * Convenience helpers for common HTTP verbs.
 */
function get(path, ttlMs = GET_CACHE_TTL_MS) {
  return cachedGet(path, ttlMs)
}

async function post(path, data) {
  const result = await request(path, { method: 'POST', body: JSON.stringify(data) })
  clearGetCache()
  return result
}

async function put(path, data) {
  const result = await request(path, { method: 'PUT', body: JSON.stringify(data) })
  clearGetCache()
  return result
}

async function patch(path, data) {
  const result = await request(path, { method: 'PATCH', body: JSON.stringify(data) })
  clearGetCache()
  return result
}

async function del(path) {
  const result = await request(path, { method: 'DELETE' })
  clearGetCache()
  return result
}

// ============================================================
// Projects
// ============================================================

const PROJECT_STATUS_LABELS = {
  active: 'Active',
  preconstruction: 'Preconstruction',
  on_hold: 'On hold',
  substantially_complete: 'Substantially complete',
  warranty: 'Warranty',
  closed: 'Closed',
  cancelled: 'Cancelled',
  inactive: 'Inactive',
}

const PROJECT_STATUS_VALUES = Object.fromEntries(
  Object.entries(PROJECT_STATUS_LABELS).map(([value, label]) => [label, value])
)

/**
 * Convert stored decimal-percent values into display percentages.
 * Project goal margin is stored as a fraction in SQL (0.1200 = 12.0%),
 * while report margin percent is already captured as a display percent.
 *
 * @param {number|string|null|undefined} value Stored percent/fraction
 * @returns {number|null} Display percent, e.g. 12.0
 */
function normalizeStoredPercent(value) {
  if (value == null || value === '') return null
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return null
  return Math.abs(numeric) <= 1 ? numeric * 100 : numeric
}

function toStoredPercent(value) {
  if (value == null || value === '') return null
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return null
  return Math.abs(numeric) > 1 ? numeric / 100 : numeric
}

function procoreProjectUrlFromId(idValue) {
  const procoreId = idValue == null || idValue === '' ? '' : String(idValue).trim()
  return procoreId ? `https://app.procore.com/${encodeURIComponent(procoreId)}/project/home` : null
}

/**
 * Normalize API project rows into the field names used by the Vue views.
 * The backend uses canonical lower-case status values and `client`; the
 * first-pass frontend was built around display labels and `clientName`.
 * Keeping the adapter here avoids leaking storage details into every view.
 *
 * @param {object|null} project Raw API project
 * @returns {object|null} View-model project
 */
function normalizeProject(project) {
  if (!project) return null
  const statusValue = project.projectStatus || 'active'
  const procoreProjectId = project.procoreProjectId ?? project.procoreId
  return {
    ...project,
    projectId: project.projectId ?? project.id,
    clientName: project.clientName ?? project.client,
    executiveName: project.executiveName ?? project.projectExecutiveName,
    pmName: project.pmName ?? project.projectManagerName,
    superName: project.superName ?? project.superintendentName,
    teamMembers: Array.isArray(project.teamMembers) ? project.teamMembers : [],
    accountingName: project.accountingName ?? project.accountingContact,
    excludedFromReporting: Boolean(project.excludedFromReporting),
    projectStatus: PROJECT_STATUS_LABELS[statusValue] || statusValue,
    projectStatusValue: statusValue,
    goalMarginPct: normalizeStoredPercent(project.goalMarginPct ?? project.goalMarginPercent),
    contractStartDate: dateInputValue(project.contractStartDate) || null,
    actualStartDate: dateInputValue(project.actualStartDate) || null,
    originalContractFinishDate: dateInputValue(project.originalContractFinishDate) || null,
    revisedCompletionDate: dateInputValue(project.revisedCompletionDate) || null,
    procoreProjectId,
    procoreProjectUrl: procoreProjectUrlFromId(procoreProjectId),
  }
}

/**
 * Convert Vue form/update fields into the backend project payload shape.
 *
 * @param {object} data View-model project fields
 * @returns {object} API payload
 */
function toProjectPayload(data) {
  const payload = { ...data }
  if (payload.clientName !== undefined && payload.client === undefined) {
    payload.client = payload.clientName
  }
  if (payload.goalMarginPct !== undefined && payload.goalMarginPercent === undefined) {
    payload.goalMarginPercent = toStoredPercent(payload.goalMarginPct)
  } else if (payload.goalMarginPercent !== undefined) {
    payload.goalMarginPercent = toStoredPercent(payload.goalMarginPercent)
  }
  if (payload.projectStatus) {
    payload.projectStatus = PROJECT_STATUS_VALUES[payload.projectStatus] || payload.projectStatus
  }
  delete payload.clientName
  delete payload.pmName
  delete payload.projectId
  delete payload.projectStatusValue
  delete payload.goalMarginPct
  return payload
}

/**
 * Fetch all projects (supports optional query params).
 *
 * @param {{ status?: string, requiresMargin?: boolean }} [filters]
 * @returns {Promise<Array>}
 */
export async function getProjects(filters = {}) {
  const params = new URLSearchParams()
  if (filters.status) params.set('status', PROJECT_STATUS_VALUES[filters.status] || filters.status)
  if (filters.requiresMargin !== undefined) params.set('requiresMarginReport', filters.requiresMargin)
  if (filters.includeExcluded !== undefined) params.set('includeExcluded', filters.includeExcluded)
  const qs = params.toString()
  const projects = await get(`/api/projects${qs ? `?${qs}` : ''}`)
  return projects.map(normalizeProject)
}

/**
 * Fetch a single project by ID.
 *
 * @param {string|number} projectId
 * @param {{ fresh?: boolean }} [options] Bypass the short-lived GET cache when current persisted values are required.
 * @returns {Promise<object>}
 */
export async function getProject(projectId, { fresh = false } = {}) {
  const path = `/api/projects/${projectId}`
  return normalizeProject(await (fresh ? request(path) : get(path)))
}

/**
 * Create a new project.
 *
 * @param {object} data Project fields
 * @returns {Promise<object>} Created project
 */
export async function createProject(data) {
  return normalizeProject(await post('/api/projects', toProjectPayload(data)))
}

/**
 * Update an existing project.
 *
 * @param {string|number} projectId
 * @param {object} data Updated fields
 * @returns {Promise<object>}
 */
export async function updateProject(projectId, data) {
  return normalizeProject(await patch(`/api/projects/${projectId}`, toProjectPayload(data)))
}

/**
 * Toggle whether a project requires a margin report.
 *
 * @param {string|number} projectId
 * @param {boolean} requires
 * @returns {Promise<object>}
 */
export async function setProjectMarginRequired(projectId, requires) {
  return normalizeProject(await patch(`/api/projects/${projectId}`, { requiresMarginReport: requires }))
}

/**
 * Toggle whether a project is excluded from Executive Reporting.
 *
 * @param {string|number} projectId
 * @param {boolean} excluded
 * @returns {Promise<object>}
 */
export async function setProjectReportingExcluded(projectId, excluded) {
  return normalizeProject(await patch(`/api/projects/${projectId}/reporting-exclusion`, { excludedFromReporting: excluded }))
}

export async function refreshProjectFromMpr(projectId) {
  const result = await post(`/api/projects/${projectId}/refresh-from-mpr`, {})
  return {
    ...result,
    project: normalizeProject(result.project),
  }
}

/** Read the current project-level monthly billing ledger. */
export async function getProjectBillingHistory(projectId) {
  return get(`/api/projects/${projectId}/billing-history`)
}

/** Read the current four-category Change Log summary for a project. */
export async function getProjectChangeLogSummary(projectId) {
  return get(`/api/projects/${projectId}/change-log-summary`, 0)
}

/** Read immutable corrections made to the project billing ledger. */
export async function getProjectBillingCorrections(projectId) {
  return get(`/api/projects/${projectId}/billing-history/corrections`)
}

/**
 * Add or correct one monthly billing period. The month is derived from the
 * full billing-through date so historical time calculations keep their cutoff.
 */
export async function saveProjectBillingPeriod(projectId, period, data) {
  return put(`/api/projects/${projectId}/billing-history/${periodKey(period)}`, {
    preconstructionAmount: data.preconstructionAmount ?? 0,
    constructionAmount: data.constructionAmount ?? 0,
    billingThroughDate: data.billingThroughDate,
    sourceReportPeriod: data.sourceReportPeriod,
  })
}

export async function deleteProjectBillingPeriod(projectId, period, reportPeriod) {
  return del(`/api/projects/${projectId}/billing-history/${periodKey(period)}?reportPeriod=${periodKey(reportPeriod)}`)
}

// ============================================================
// Dashboard
// ============================================================

/**
 * Fetch dashboard summary rows for the current period.
 * Returns project status, deltas, and last-submitted metadata.
 *
 * @returns {Promise<Array>}
 */
export async function getDashboard() {
  const now = new Date()
  const period = new Date(Date.UTC(now.getFullYear(), now.getMonth(), 1))
    .toISOString()
    .slice(0, 10)

  const dashboard = await get(`/api/dashboard?period=${period}`)

  return (dashboard.rows || []).map((row) => {
    const requiresMarginReport = Boolean(row.project?.requiresMarginReport)
    const hasSummary = Boolean(row.summary)
    const hasMarginReport = Boolean(row.marginReport)
    const hasDraft = Boolean(row.draft)
    const reportStatus = !hasSummary && hasDraft
      ? 'Draft'
      : !hasSummary
      ? 'Missing'
      : requiresMarginReport && !hasMarginReport
        ? 'Partial'
        : 'Submitted'

    const reviewWarnings = reportCompletenessWarnings({
      goalMarginPercent: row.project?.goalMarginPercent,
      contractStartDate: row.project?.contractStartDate,
      originalContractFinishDate: row.project?.originalContractFinishDate,
      financialStatus: row.summary?.financialStatus,
      scheduleStatus: row.summary?.scheduleStatus,
      currentContractValue: row.marginReport?.currentContractValue ?? row.summary?.currentContractValue,
      projectedFinalCost: row.marginReport?.projectedFinalCost,
      marginDollars: row.marginReport?.marginDollars,
      marginPercent: row.marginReport?.marginPercent,
      projectedFinishDate: row.marginReport?.projectedFinishDate,
      yearProjections: row.marginReport?.yearProjections || [],
    })

    return {
      projectId: row.project?.id,
      projectName: row.project?.projectName,
      jobNumber: row.project?.jobNumber,
      pmName: row.project?.projectManagerName,
      teamMembers: Array.isArray(row.project?.teamMembers) ? row.project.teamMembers : [],
      // Explicit MPR role email is authoritative. The ID fallback only supports
      // rows synced before the separate email column was introduced.
      pmEmail: looksLikeEmail(row.project?.projectManagerEmail)
        ? row.project.projectManagerEmail
        : (looksLikeEmail(row.project?.projectManagerId) ? row.project.projectManagerId : ''),
      requiresMarginReport,
      reportStatus,
      reviewState: normalizeReviewState(row.reviewState),
      reviewStatus: reviewWorkflowStatus(row.reviewState, { hasSummary, hasDraft }),
      reviewWarnings,
      hasSummary,
      hasMarginReport,
      hasDraft,
      clientHealth: row.summary?.clientSatisfaction,
      overallStatus: row.summary?.overallStatus,
      financialStatus: row.summary?.financialStatus,
      scheduleStatus: row.summary?.scheduleStatus,
      contractValueWithCOs: row.marginReport?.currentContractValue ?? row.summary?.currentContractValue,
      billingsToDate: row.summary?.billingsToDate,
      billingPercentComplete: calculateBillingPercentComplete({
        billingsToDate: row.summary?.billingsToDate,
        contractValueWithCOs: row.marginReport?.currentContractValue ?? row.summary?.currentContractValue,
      }),
      timePercentComplete: calculateTimePercentComplete({
        contractStartDate: row.project?.contractStartDate || row.project?.actualStartDate,
        actualStartDate: row.project?.actualStartDate,
        originalContractFinishDate: row.project?.originalContractFinishDate,
        daysAddedByChangeOrder: row.marginReport?.daysAddedByChangeOrder,
      }),
      contractStartDate: row.project?.contractStartDate,
      contractedFinishDate: row.project?.originalContractFinishDate,
      actualStartDate: row.project?.actualStartDate,
      projectedFinishDate: row.marginReport?.projectedFinishDate,
      goalMarginPct: normalizeStoredPercent(row.project?.goalMarginPercent),
      marginPct: row.marginReport?.marginPercent,
      marginDelta: row.deltas?.marginPercent?.diff,
      lastSubmitted: row.summary?.submittedAt || row.marginReport?.submittedAt,
      lastSubmittedByName: row.summary?.submittedByName || row.marginReport?.submittedByName,
      editedAt: row.summary?.editedAt || row.marginReport?.editedAt || row.draft?.savedAt,
      editedByName: row.summary?.editedByName || row.marginReport?.editedByName || row.draft?.savedByName,
      draftSavedAt: row.draft?.savedAt,
      noteCount: row.noteCount,
      raw: row
    }
  })
}

/** Admin-only historical dataset for the Billing Forecast Lab prototype. */
export async function getBillingForecastLabData() {
  return get('/api/billing-forecast-lab/data')
}

// ============================================================
// Reminder templates
// ============================================================

export const PM_SUBMISSION_REMINDER_TEMPLATE_KEY = 'pm_submission_follow_up'

export async function getReminderTemplate(templateKey = PM_SUBMISSION_REMINDER_TEMPLATE_KEY) {
  return get(`/api/reminder-templates/${encodeURIComponent(templateKey)}`)
}

export async function updateReminderTemplate(templateKey, data) {
  return put(`/api/reminder-templates/${encodeURIComponent(templateKey)}`, data)
}

export async function getReminderSettings() {
  return get('/api/reminder-settings')
}

export async function updateReminderSettings(data) {
  return put('/api/reminder-settings', data)
}

export async function previewPmSubmissionReminderEmails(data) {
  return post('/api/reminder-emails/pm-submission-follow-up/preview', data)
}

export async function sendPmSubmissionReminderEmails(data) {
  return post('/api/reminder-emails/pm-submission-follow-up/send', data)
}

export async function getReminderEmailHistory(limit = 10) {
  return get(`/api/reminder-emails/history?limit=${encodeURIComponent(limit)}`)
}

// ============================================================
// Reports
// ============================================================

function periodToDate(period) {
  if (!period) return period
  return /^\d{4}-\d{2}$/.test(period) ? `${period}-01` : period
}

function periodKey(period) {
  if (!period) return ''
  return String(period).slice(0, 7)
}

/**
 * Normalize API/SQL date values for native HTML date controls.
 * SQL-backed endpoints may return ISO timestamps even for DATE columns, while
 * input[type="date"] only displays YYYY-MM-DD values.
 */
function dateInputValue(value) {
  if (!value) return ''
  return String(value).slice(0, 10)
}

function yesNoToBool(value) {
  if (value === true || value === false || value == null) return value
  const normalized = String(value).trim().toLowerCase()
  if (['yes', 'y', 'true', '1'].includes(normalized)) return true
  if (['no', 'n', 'false', '0'].includes(normalized)) return false
  return null
}

export function reportCompletenessWarnings(data = {}) {
  const warnings = []
  const missing = (value) => value == null || value === ''

  if (missing(data.reportingMarginPct ?? data.marginPercent)) warnings.push('Missing Reporting Margin %')
  if (missing(data.goalMarginPct ?? data.goalMarginPercent)) warnings.push('Missing Goal Margin %')
  if (missing(data.marginDollars)) warnings.push('Missing Projected Margin $')
  if (missing(data.projectedFinalCost)) warnings.push('Missing Projected Final Cost')
  if (missing(data.financialStatus)) warnings.push('Missing Financial Status')
  if (missing(data.scheduleStatus)) warnings.push('Missing Schedule Status')
  if (missing(data.contractStartDate)) warnings.push('Missing Contract Start Date')
  if (missing(data.originalContractFinish ?? data.originalContractFinishDate ?? data.contractedFinishDate)) warnings.push('Missing Contracted Finish')
  if (missing(data.projectedFinishDate)) warnings.push('Missing PER Projected Finish Date')
  return warnings
}

function normalizeReviewState(reviewState) {
  if (!reviewState) return null
  return {
    ...reviewState,
    reviewStatus: reviewState.reviewStatus || reviewState.status || 'submitted',
    reviewNote: reviewState.reviewNote || '',
  }
}

export function reviewWorkflowStatus(reviewState, { hasSummary = true, hasDraft = false } = {}) {
  if (!hasSummary && hasDraft) return 'Draft'
  if (!hasSummary) return 'Missing'

  const status = normalizeReviewState(reviewState)?.reviewStatus || 'submitted'
  return {
    submitted: 'Awaiting Review',
    needs_pm_follow_up: 'Needs PM Follow-up',
    reviewed: 'Reviewed',
  }[status] || 'Awaiting Review'
}

function normalizeReport(summary = null, margin = null, reviewState = null) {
  const reportPeriod = summary?.reportPeriod || margin?.reportPeriod
  const currentContractValue = summary?.currentContractValue ?? margin?.currentContractValue
  const costsAtBillingDate = summary?.costsAtBillingDate ?? margin?.projectedFinalCost
  const billingsToDate = summary?.billingsToDate

  const normalized = {
    ...(summary || {}),
    ...(margin || {}),
    summaryId: summary?.id,
    marginReportId: margin?.id,
    period: periodKey(reportPeriod),
    reportPeriod,
    financialStatus: summary?.financialStatus,
    scheduleStatus: summary?.scheduleStatus,
    overallStatus: summary?.overallStatus || summary?.overallHealth,
    originalContractValue: margin?.originalContractValue,
    originalContractValueSource: margin?.originalContractValueSource,
    contractValueWithCOs: currentContractValue,
    currentContractValue,
    currentContractValueSource: margin?.currentContractValueSource,
    billingsToDate,
    costsAtBillingDate,
    cashFlow: billingsToDate == null || costsAtBillingDate == null ? null : billingsToDate - costsAtBillingDate,
    goalMarginPct: normalizeStoredPercent(summary?.goalMarginPct),
    projectedFinalCost: margin?.projectedFinalCost,
    remainingContingency: margin?.remainingContingency,
    marginDollars: margin?.marginDollars,
    reportingMarginPct: margin?.marginPercent,
    marginPct: margin?.marginPercent,
    marginYears: margin?.yearProjections || [],
    openChangesValue: summary?.openChangesValue ?? margin?.changeOrderValue,
    daysAddedByCO: margin?.daysAddedByChangeOrder,
    projectedFinishDate: dateInputValue(margin?.projectedFinishDate),
    marginReportComment: margin?.marginReportComment || '',
    dateOfLastBilling: dateInputValue(summary?.dateOfLastBilling),
    billingHistoryComment: summary?.billingHistoryComment || '',
    billingForecastSnapshot: summary?.billingForecastSnapshot || null,
    ownerScheduleProjectedFinish: summary?.projectedFinishDateOwner,
    subScheduleProjectedFinish: summary?.projectedFinishDateSub,
    lastOwnerPayment: summary?.lastOwnerPaymentTiming,
    highRiskActivitiesUpcoming: summary?.highRiskActivities,
    lastPmInspectionDate: summary?.lastPMInspectionDate,
    dailyReportsUpToDate: summary?.dailyReportsUpToDate == null ? '' : (summary.dailyReportsUpToDate ? 'Yes' : 'No'),
    drawingsUpToDate: summary?.drawingsUpToDate == null ? '' : (summary.drawingsUpToDate ? 'Yes' : 'No'),
    subConcerns: summary?.subcontractorConcerns || [],
    submittedById: summary?.submittedById || margin?.submittedById,
    submittedByName: summary?.submittedByName || margin?.submittedByName,
    submittedAt: summary?.submittedAt || margin?.submittedAt,
    editedById: summary?.editedById || margin?.editedById,
    editedByName: summary?.editedByName || margin?.editedByName,
    editedAt: summary?.editedAt || margin?.editedAt,
    reviewState: normalizeReviewState(reviewState),
  }
  normalized.reviewWarnings = reportCompletenessWarnings(normalized)
  normalized.reviewStatus = reviewWorkflowStatus(normalized.reviewState, { hasSummary: Boolean(summary), hasDraft: false })
  return normalized
}

function normalizeDraft(draft) {
  if (!draft) return null
  return {
    ...(draft.payload || {}),
    draftId: draft.id,
    isDraft: true,
    period: periodKey(draft.reportPeriod),
    reportPeriod: draft.reportPeriod,
    draftSavedAt: draft.savedAt,
    savedByName: draft.savedByName,
    reviewStatus: 'Draft',
  }
}

async function getReportParts(projectId) {
  // Both list endpoints now return year projections included via JOIN in the API.
  // No secondary per-report fetches needed.
  const [summaries, margins, drafts, reviewStates] = await Promise.all([
    get(`/api/projects/${projectId}/exec-summaries`),
    get(`/api/projects/${projectId}/margin-reports`),
    get(`/api/projects/${projectId}/report-drafts`),
    get(`/api/projects/${projectId}/review-states`),
  ])
  return { summaries, margins, drafts, reviewStates: reviewStates.map(normalizeReviewState) }
}

/**
 * Fetch all reports for a project by merging executive summaries and margin reports.
 *
 * @param {string|number} projectId
 * @returns {Promise<Array>}
 */
export async function getReports(projectId) {
  const { summaries, margins, drafts, reviewStates } = await getReportParts(projectId)
  const periods = new Set([
    ...summaries.map((s) => periodKey(s.reportPeriod)),
    ...margins.map((m) => periodKey(m.reportPeriod)),
    ...drafts.map((d) => periodKey(d.reportPeriod)),
  ].filter(Boolean))

  return [...periods].map((period) => {
    const report = normalizeReport(
      summaries.find((s) => periodKey(s.reportPeriod) === period),
      margins.find((m) => periodKey(m.reportPeriod) === period),
      reviewStates.find((state) => periodKey(state.reportPeriod) === period)
    )
    const draft = normalizeDraft(drafts.find((d) => periodKey(d.reportPeriod) === period))
    return report.period ? { ...report, draftSavedAt: draft?.draftSavedAt, hasDraft: Boolean(draft) } : draft
  }).filter(Boolean)
}

/**
 * Fetch a single report by project + period.
 * Period format: YYYY-MM (e.g. "2026-05")
 *
 * @param {string|number} projectId
 * @param {string} period  YYYY-MM
 * @returns {Promise<object>}
 */
export async function getReport(projectId, period) {
  const reports = await getReports(projectId)
  const report = reports.find((r) => r.period === periodKey(period))
  if (!report) throw new ApiError(404, 'Report not found')
  const throughSelectedPeriod = reports
    .filter((row) => !row.isDraft && row.period <= report.period)
    .sort((a, b) => (a.period > b.period ? 1 : -1))
  return {
    ...report,
    billingHistory: throughSelectedPeriod.filter((row) => row.billingsToDate != null),
    reportHistory: throughSelectedPeriod.filter((row) => row.marginReportId != null),
  }
}

function draftStorageKey(projectId, period) {
  return `exec-report-draft:${projectId}:${periodKey(period)}`
}

function readLocalDraft(projectId, period) {
  if (typeof window === 'undefined' || !window.localStorage) return null
  const raw = window.localStorage.getItem(draftStorageKey(projectId, period))
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch {
    window.localStorage.removeItem(draftStorageKey(projectId, period))
    return null
  }
}

function writeLocalDraft(projectId, period, data) {
  if (typeof window === 'undefined' || !window.localStorage) return
  window.localStorage.setItem(draftStorageKey(projectId, period), JSON.stringify({
    ...data,
    period: periodKey(period),
    reportPeriod: periodToDate(period),
    draftSavedAt: new Date().toISOString(),
  }))
}

function removeLocalDraft(projectId, period) {
  if (typeof window === 'undefined' || !window.localStorage) return
  window.localStorage.removeItem(draftStorageKey(projectId, period))
}

async function getServerDraft(projectId, period) {
  return normalizeDraft(await get(`/api/projects/${projectId}/report-drafts/${periodKey(period)}`))
}

async function deleteServerDraft(projectId, period) {
  return del(`/api/projects/${projectId}/report-drafts/${periodKey(period)}`)
}

/**
 * Initialize a report form from the latest submitted report when available.
 *
 * @param {string|number} projectId
 * @param {string} period  YYYY-MM
 * @returns {Promise<object>} Initialized unsaved report data
 */
export async function initReport(projectId, period) {
  const reports = await getReports(projectId)
  const billingHistory = reports
    .filter((report) => report.period < periodKey(period) && !report.isDraft && report.billingsToDate != null)
    .sort((a, b) => (a.period > b.period ? 1 : -1))
  const reportHistory = reports
    .filter((report) => report.period < periodKey(period) && report.marginReportId != null)
    .sort((a, b) => (a.period > b.period ? 1 : -1))
  const existing = reports.find((r) => r.period === periodKey(period))

  const carriedForward = !existing ? reports
    .filter((r) => r.period < periodKey(period))
    .sort((a, b) => (a.period < b.period ? 1 : -1))[0] : null

  const carriedForwardData = carriedForward ? {
    ...carriedForward,
    marginYears: (carriedForward.marginYears || []).map(({ id, marginReportId, ...row }) => ({ ...row })),
    subConcerns: (carriedForward.subConcerns || []).map(({ id, executiveSummaryId, ...row }) => ({ ...row })),
  } : {}

  const baseReport = existing || {
    ...carriedForwardData,
    summaryId: null,
    marginReportId: null,
    id: null,
    carriedForwardFromPeriod: carriedForward?.period || null,
    carryForwardSourceSubmittedAt: carriedForward?.submittedAt || null,
    period: periodKey(period),
    reportPeriod: periodToDate(period),
    submittedAt: null,
    editedAt: null,
  }

  const serverDraft = await getServerDraft(projectId, period)
  const draft = serverDraft || readLocalDraft(projectId, period)
  if (!draft) return { ...baseReport, reportHistory, billingHistory }

  return {
    ...baseReport,
    ...draft,
    // Keep persisted IDs from an existing submitted report even if the local
    // draft was saved before the IDs were present. Drafts must never turn an
    // edit into a duplicate POST.
    summaryId: existing?.summaryId ?? draft.summaryId ?? baseReport.summaryId ?? null,
    marginReportId: existing?.marginReportId ?? draft.marginReportId ?? baseReport.marginReportId ?? null,
    draftLoaded: true,
    draftSource: serverDraft ? 'server' : 'browser',
    reportHistory,
    billingHistory,
  }
}

function toMarginReportPayload(period, data) {
  return {
    reportPeriod: periodToDate(period),
    originalContractValue: data.originalContractValue,
    originalContractValueSource: data.originalContractValueSource,
    currentContractValue: data.contractValueWithCOs ?? data.currentContractValue,
    currentContractValueSource: data.currentContractValueSource,
    acceptProjectContractValues: data.acceptProjectContractValues === true,
    changeOrderValue: data.openChangesValue,
    projectedFinalCost: data.projectedFinalCost ?? data.costsAtBillingDate ?? null,
    remainingContingency: data.remainingContingency,
    // Margin dollars are tracked at the whole-project level. Year rows only
    // represent projected revenue timing, so do not derive margin from them.
    marginDollars: data.marginDollars ?? (
      data.projectedFinalCost != null && (data.contractValueWithCOs ?? data.currentContractValue) != null
        ? Number(data.contractValueWithCOs ?? data.currentContractValue) - Number(data.projectedFinalCost)
        : null
    ),
    marginPercent: data.reportingMarginPct ?? data.marginPercent,
    daysAddedByChangeOrder: data.daysAddedByCO ?? data.daysAddedByChangeOrder,
    projectedFinishDate: data.projectedFinishDate || null,
    marginReportComment: data.marginReportComment || null,
    yearProjections: (data.marginYears || data.yearProjections || []).map((row, index) => ({
      year: row.year ? Number(row.year) : null,
      salesDollars: row.salesDollars,
      marginDollars: null,
      sortOrder: index,
    })).filter((row) => row.year && row.salesDollars != null),
  }
}

function toExecutiveSummaryPayload(period, data, marginReportId) {
  return {
    reportPeriod: periodToDate(period),
    marginReportId,
    overallStatus: data.overallStatus || null,
    financialStatus: data.financialStatus || null,
    scheduleStatus: data.scheduleStatus || null,
    overallHealth: data.overallHealth || data.overallStatus || null,
    currentContractValue: data.contractValueWithCOs ?? data.currentContractValue,
    billingsToDate: data.billingsToDate,
    dateOfLastBilling: data.dateOfLastBilling || null,
    billingHistoryComment: data.billingHistoryComment || null,
    costsAtBillingDate: data.costsAtBillingDate,
    lastOwnerPaymentTiming: data.lastOwnerPayment || data.lastOwnerPaymentTiming || null,
    openChangesCount: data.openChangesCount,
    openChangesValue: data.openChangesValue,
    changeLogSnapshot: data.changeLogSnapshot,
    projectedFinishDateOwner: data.ownerScheduleProjectedFinish || data.projectedFinishDateOwner || null,
    projectedFinishDateSub: data.subScheduleProjectedFinish || data.projectedFinishDateSub || null,
    lastScheduleSentOwner: data.lastScheduleSentOwner || null,
    lastScheduleSentSubs: data.lastScheduleSentSubs || null,
    scheduleRisks: data.scheduleRisks || null,
    internalTeamHealth: data.internalTeamHealth || null,
    additionalStaffingNeeded: data.additionalStaffingNeeded || null,
    clientSatisfaction: data.clientSatisfaction || null,
    designTeamSatisfaction: data.designTeamSatisfaction || null,
    clientRelationsNotes: data.clientRelationsNotes || null,
    execActionItems: data.execActionItems || null,
    lastSuperInspectionDate: data.lastSuperInspectionDate || null,
    lastPMInspectionDate: data.lastPmInspectionDate || data.lastPMInspectionDate || null,
    lastTailgateMeetingDate: data.lastTailgateMeetingDate || null,
    highRiskActivities: data.highRiskActivitiesUpcoming || data.highRiskActivities || null,
    qualityRisks: data.qualityRisks || null,
    currentQualityIssues: data.currentQualityIssues || null,
    dailyReportsUpToDate: yesNoToBool(data.dailyReportsUpToDate),
    drawingsUpToDate: yesNoToBool(data.drawingsUpToDate),
    billingForecastSnapshot: data.billingForecastSnapshot,
    subcontractorConcerns: (data.subConcerns || data.subcontractorConcerns || []).map((row, index) => ({
      subcontractorName: row.subcontractorName || row.subName,
      correctiveAction: row.correctiveAction || row.concern,
      sortOrder: index,
    })).filter((row) => row.subcontractorName),
  }
}

/**
 * Save a server-side draft for the current report.
 *
 * @param {string|number} projectId
 * @param {string} period
 * @param {object} data Report payload
 * @returns {Promise<object>}
 */
export async function saveReport(projectId, period, data) {
  const payload = {
    ...data,
    period: periodKey(period),
    reportPeriod: periodToDate(period),
  }
  const draft = await put(`/api/projects/${projectId}/report-drafts/${periodKey(period)}`, { payload })
  removeLocalDraft(projectId, period)
  return { ...payload, draftSavedAt: draft.savedAt, savedByName: draft.savedByName }
}

/**
 * Submit a finalized report by writing margin report + executive summary rows.
 *
 * MPR-owned project metadata is synchronized through the server-side MPR bridge,
 * not updated from report submission. The report payload may snapshot selected
 * project values for period reporting, but the project record itself remains
 * owned by MPR.
 *
 * @param {string|number} projectId
 * @param {string} period
 * @param {object} data Report payload
 * @returns {Promise<object>}
 */
export async function submitReport(projectId, period, data) {
  const marginPayload = toMarginReportPayload(period, data)
  const margin = data.marginReportId
    ? await patch(`/api/projects/${projectId}/margin-reports/${data.marginReportId}`, marginPayload)
    : await post(`/api/projects/${projectId}/margin-reports`, marginPayload)

  const summaryPayload = toExecutiveSummaryPayload(period, data, margin?.id)
  const summary = data.summaryId
    ? await patch(`/api/projects/${projectId}/exec-summaries/${data.summaryId}`, summaryPayload)
    : await post(`/api/projects/${projectId}/exec-summaries`, summaryPayload)

  await deleteServerDraft(projectId, period)
  removeLocalDraft(projectId, period)
  return normalizeReport(summary, margin)
}

// ============================================================
// Notes (exec comments + PM replies)
// ============================================================

function normalizeNote(note) {
  if (!note) return note
  return {
    ...note,
    body: note.body ?? note.noteText ?? '',
    replyToId: note.replyToId ?? note.parentNoteId ?? null,
  }
}

/**
 * Fetch notes for a report.
 *
 * @param {string|number} projectId
 * @param {string} period
 * @returns {Promise<Array>}
 */
export async function getNotes(projectId, period) {
  const notes = await get(`/api/projects/${projectId}/notes?period=${periodToDate(period)}`)
  return notes.map(normalizeNote)
}

/**
 * Add a note to a report.
 *
 * @param {string|number} projectId
 * @param {string} period
 * @param {{ body: string, replyToId?: number }} data
 * @returns {Promise<object>}
 */
export async function addNote(projectId, period, data) {
  return normalizeNote(await post(`/api/projects/${projectId}/notes`, {
    reportPeriod: periodToDate(period),
    noteText: data.noteText || data.body,
    parentNoteId: data.parentNoteId || data.replyToId || null,
  }))
}

export async function setReportReviewState(projectId, period, data) {
  return normalizeReviewState(await put(`/api/projects/${projectId}/review-states/${periodKey(period)}`, {
    reviewStatus: data.reviewStatus || data.status,
    reviewNote: data.reviewNote || data.note || '',
  }))
}

// ============================================================
// Lookups
// ============================================================

const LOOKUP_CATEGORY_KEYS = {
  OverallStatus: 'overallStatus',
  overallStatus: 'overallStatus',
  FinancialStatus: 'financialStatus',
  financialStatus: 'financialStatus',
  ScheduleStatus: 'scheduleStatus',
  scheduleStatus: 'scheduleStatus',
  TeamHealth: 'teamHealth',
  teamHealth: 'teamHealth',
  OverallHealth: 'overallHealth',
  overallHealth: 'overallHealth',
  PaymentTiming: 'ownerPayment',
  OwnerPayment: 'ownerPayment',
  ownerPayment: 'ownerPayment',
  Satisfaction: 'satisfaction',
  satisfaction: 'satisfaction',
  YesNo: 'yesNo',
  yesNo: 'yesNo',
  ProjectStatus: 'projectStatus',
  projectStatus: 'projectStatus',
}

function normalizeLookupOption(option) {
  if (typeof option === 'string') return option
  return option?.displayLabel || option?.label || option?.value || ''
}

function normalizeLookupMap(raw) {
  const normalized = {}
  for (const [category, options] of Object.entries(raw || {})) {
    const key = LOOKUP_CATEGORY_KEYS[category] || category
    normalized[key] = (options || [])
      .map(normalizeLookupOption)
      .filter(Boolean)
  }
  return normalized
}

/**
 * Fetch all lookup tables (status options, dropdown values, etc.).
 *
 * @returns {Promise<object>} Map of lookup category -> array of display labels
 */
export async function getLookups() {
  return normalizeLookupMap(await get('/api/lookups'))
}
